import { transparentize } from "polished";
import { css } from "styled-components";

/**
 * Shared look for text inputs, text areas, selects and input-like boxes:
 * white, a quiet border that darkens on hover, and a soft ring in the accent
 * colour on focus. Height matches a default Button (36px).
 */

export const FIELD_HEIGHT = 36;

/** The focus ring, for use on :focus or :focus-within. */
export const fieldFocus = css`
  outline: 0;
  border-color: ${(p) => p.theme.colors.primary};
  box-shadow: 0 0 0 3px ${(p) => transparentize(0.75, p.theme.colors.primary)};
`;

/** Error state: a red border and ring. */
export const fieldInvalid = css`
  border-color: ${(p) => p.theme.colors.danger};
  box-shadow: 0 0 0 3px ${(p) => transparentize(0.85, p.theme.colors.danger)};
`;

/**
 * The frame itself. `focusSelector` is ":focus" for a bare input and
 * ":focus-within" for a wrapper around one.
 */
export const fieldFrame = (focusSelector: ":focus" | ":focus-within") => css`
  border: 1px solid ${(p) => p.theme.colors.borderStrong};
  border-radius: ${(p) => p.theme.borderRadius.md}px;
  background-color: ${(p) => p.theme.colors.surface};
  color: ${(p) => p.theme.colors.text};
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease,
    background-color 0.15s ease;

  &:hover {
    border-color: ${(p) => p.theme.colors.grey};
  }

  &${focusSelector} {
    ${fieldFocus};
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

/** Placeholder and disabled/read-only text for a bare input or textarea. */
export const fieldText = css`
  &::placeholder {
    color: ${(p) => p.theme.colors.grey2};
    opacity: 1;
  }

  &:disabled {
    background-color: ${(p) => p.theme.colors.sunken};
    border-color: ${(p) => p.theme.colors.border};
    color: ${(p) => p.theme.colors.textMuted};
    cursor: not-allowed;
  }

  &:read-only:not(:disabled) {
    background-color: ${(p) => p.theme.colors.grey7};
  }

  /* Only after the user has touched the field, not on page load. */
  &:user-invalid {
    ${fieldInvalid};
  }

  &[aria-invalid="true"] {
    ${fieldInvalid};
  }
`;
